import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getAffiliateConversions } from '@/modules/Affiliate/services'
import { AffiliateConversion } from '@/modules/Affiliate/types'
import { formatFromUTC } from '@/utils/function'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        maximumFractionDigits: 0,
    }).format(value)

interface ConversionsProps {
    affiliateId: number
}

const Conversions: React.FC<ConversionsProps> = ({ affiliateId }) => {
    const { t } = useTranslation('affiliate')

    const { params, queryParams, setParam, searchInputProps } =
        useUrlSearchParams({
            defaults: { page: 1, page_size: 10 },
        })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.AFFILIATE_CONVERSIONS,
            affiliateId,
            queryParams,
        ] as const,
        queryFn: () =>
            getAffiliateConversions(affiliateId, {
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
            }),
        enabled: !!affiliateId,
        placeholderData: true,
    })

    const conversions = data?.results || []
    const pagination = data?.pagination
    const stateComponent = renderState()

    const columns: DataTableColumn<AffiliateConversion>[] = useMemo(
        () => [
            {
                key: 'click_id',
                title: t('click_id'),
                width: 100,
            },
            {
                key: 'customer_id',
                title: t('customer_id'),
                width: 120,
                formatter: {
                    type: 'link',
                    pathKey: 'customerDetail',
                    pathParams: (value) => ({ id: String(value) }),
                },
            },
            {
                key: 'customer_name',
                title: t('customer_name'),
                width: 200,
                formatter: {
                    type: 'link',
                    pathKey: 'customerDetail',
                    pathParams: (_value, row) => ({
                        id: String(row.customer_id),
                    }),
                },
            },
            {
                key: 'customer_email',
                title: t('email'),
                width: 200,
            },
            {
                key: 'total_revenue',
                title: t('revenue'),
                width: 140,
                render: (value) => formatCurrency(value || 0),
            },
            {
                key: 'total_commission',
                title: t('commission'),
                width: 140,
                render: (value) => (
                    <span className="font-semibold">
                        {formatCurrency(value || 0)}
                    </span>
                ),
            },
            {
                key: 'commission_status',
                title: t('status'),
                width: 120,
                render: (value) => value || '-',
            },
            {
                key: 'converted_at',
                title: t('converted_at'),
                width: 160,
                render: (value) => formatFromUTC(value, 'DD/MM/YYYY HH:mm'),
            },
            {
                key: 'customer_created_at',
                title: t('customer_created_at'),
                width: 160,
                render: (value) => formatFromUTC(value, 'DD/MM/YYYY HH:mm'),
            },
        ],
        [t]
    )

    const handlePageChange = (page: number) => setParam('page', page, false)
    const handlePageSizeChange = (size: number) => {
        setParam('page_size', size)
        setParam('page', 1)
    }

    return (
        <Paper className="space-y-2 my-2">
            {stateComponent || (
                <DataTableCustomized<AffiliateConversion>
                    columns={columns}
                    rows={conversions}
                    rowKey={(row) => row.click_id}
                    size="small"
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search_affiliate'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'affiliate-conversions',
                        },
                    }}
                    stickyHeader={{
                        enabled: true,
                        offsetTopSelector: '#main-header',
                    }}
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                    }}
                />
            )}
        </Paper>
    )
}

export default Conversions
